import * as React from 'react';

import Menu from '@mui/material/Menu';
import { alpha, styled } from '@mui/material/styles';

import { IconButton, Tooltip } from "@mui/material";
import { ReactComponent as InternalTags } from './icons/internal_tags.svg';
import { ReactComponent as InternalTagsDisabled } from './icons/internal_tags_disabled.svg';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { DataAPIContext, CREATE, UPDATE } from '../api';
import { useSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import useCloseOnPanelChange from './utils/useCloseOnPanelChange';
import { cv } from '../themeVars';

const StyledMenu = styled((props) => (
    <Menu
        elevation={0}
        // CP-6445: MUI locks body scroll while open; that lock can outlive the menu
        // when a route change switches panels without a dismissing click.
        disableScrollLock
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
        }}
        {...props}
    />
))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: theme.spacing(1),
        minWidth: 180,
        maxWidth: 330,
        backgroundColor: cv('surface', "#FCFCFE"),
        color:
            theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
        boxShadow:
            'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
            padding: '4px 0',
        },
        '& .MuiMenuItem-root': {
            paddingTop: "2px",
            paddingBottom: "1px",
            '& .MuiSvgIcon-root': {
                fontSize: 18,
                //color: theme.palette.text.secondary,
                //marginRight: theme.spacing(1.5),
            },
            '&:active': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    theme.palette.action.selectedOpacity,
                ),
            },
        },
        '& .MuiFormControlLabel-root': {
            width: "120%",
            marginRight: 0, 
            paddingRight: 16,
            '& .MuiTypography-root': {
                fontSize: ".9em",
                fontWeight: 500,
            },
            
        },
    },
    '& .MuiInputBase-root': {
        '& .MuiAutocomplete-input': {
            width: "fit-content !important"
        },
    },

}));

export default function InternalTagsMenu(props) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [tagSearchValue, setTagSearchValue] = React.useState(null);
    const [, setOptionsCount] = React.useState(null);
    const [options, setOptions] = React.useState([]);
    const [refreshKey, setRefreshKey] = React.useState(0);
    const [, setInternalTags] = React.useState(props.internalTags || [])
    let internalTagsRef = React.useRef(props.internalTags || []);
    const tour_tag_resource = "tour_tag"
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const open = Boolean(anchorEl);
    const buttonRef = React.useRef(null);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
        props.setForceInternalTagsClick?.(null)
    };

    useCloseOnPanelChange(props.selectedScreen, handleClose);

    const onTagsChange = async (ev, tags, reason) => {
        updateTags(tags)
    };

    const updateTags = async (tags) => {
        const conversation_unique_name = props.channelUniqueName
        const res = await dataAPI(UPDATE, `chat/conversation_internal_tags`, {
            id: conversation_unique_name,
            data: {
                tags: tags.map((tag) => tag.id),
            }
        });
        if (res.statusCode === 200 || res.statusCode === 201) {
            let diff = tags.filter((e) => !internalTagsRef.current.some(r => e.id === r.id));
            if(diff.length > 0){
                enqueueSnackbar('"' + diff[0].name + '" tag added', { variant: 'success' });
            }
            else{
                diff = internalTagsRef.current.filter((e) => !tags.some(r => e.id === r.id));
                if(diff.length > 0){
                    enqueueSnackbar('"' + diff[0].name + '" tag removed', { variant: 'success' });
                }
            }
            internalTagsRef.current = tags;
            //setInternalTags(tags);
            props.setInternalTags(tags)
        } else {
            enqueueSnackbar('Tags could not be updated', { variant: 'error' });
        }
    }

    React.useEffect(() => {
        setInternalTags(props.internalTags)
    }, [props.internalTags]);

    React.useEffect(() => {
        //alert(props.forceInternalTagsClick)
        props.forceInternalTagsClick && buttonRef.current.click()
        
    }, [props.forceInternalTagsClick])

    return (
        <span>
            <Tooltip title={'Add Internal Tags'} arrow placement="top"><IconButton id="demo-customized-button"
                aria-controls={open ? 'demo-customized-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                ref={buttonRef}
                onClick={handleClick}
                variant="contained"
                style={{ cursor: props.channelSubjectID ? 'pointer' : "default", marginLeft: props.style?.marginLeft || "-.45rem", width: "2.5rem", height: "2.5rem"}}
            >
                {props.channelSubjectID ? <InternalTags style={{ width: 20, height: 20, marginLeft: -2 }} /> : <InternalTagsDisabled style={{ width: 20, height: 20, marginLeft: -2 }} />}
            </IconButton></Tooltip>

            <StyledMenu
                id="demo-customized-menu"
                MenuListProps={{
                    'aria-labelledby': 'demo-customized-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                <div style={{ textAlign: "left", fontWeight: 600, margin: "16px", minWidth: "300px" }}>
                    {/* add a junk tag since this key press causes outline bug in mui*/}
                    <span style={{position:"absolute", color: "rgb(0,0,0,0)"}}>³</span>
                    <div>Internal Tags</div>
                    <RemoteAutocomplete
                        disabled={props.disabled}
                        multiple
                        resource={tour_tag_resource}
                        apiFilters={[{ field: 'category_code', op: 'equals', value: "chat_internal"}]}
                        urlSuffix={null} 
                        label={tagSearchValue?.trim().length &&
                            props.internalTags?.find((t) => t.name.toLowerCase() === tagSearchValue?.trim().toLowerCase()) == null &&
                            options?.find((t) => t.name.toLowerCase() === tagSearchValue?.trim().toLowerCase()) == null ? "(Pressing Enter will add \"" + tagSearchValue?.trim() + "\")" : "Internal Tags"}
                        variant={props.variant || 'filled'} size={props.size || 'small'}
                        fullWidth
                        filterSelected
                        value={props.internalTags || []}
                        defaultValue={props.internalTags || []}
                        renderOption={React.useCallback((props, option, { inputValue }) => {
                            function boldString(str, substr) {
                                const strRegExp = new RegExp(substr, 'ig')
                                return str.replace(strRegExp, '<b style=color:black !important;>$&</b>')
                            }
                            return (
                                <Box component='li' {...props}>
                                    <div style={{}} dangerouslySetInnerHTML={{ __html: boldString(option.name, inputValue) }}/>
                                </Box>
                            )
                        })}
                        onChange={onTagsChange}
                        helperText={props.helperText}
                        style={{marginTop: "16px"}}
                        onQueryChange={setTagSearchValue}
                        clearOnBlur={false}
                        noOptionsText={props.internalTags?.find((t) => t.name.toLowerCase() === tagSearchValue?.trim().toLowerCase()) != null ? `"${tagSearchValue.trim()}" already added` : tagSearchValue?.trim() ? `Press Enter to add "${tagSearchValue}"` : "No matches"}
                        setOptionsCount={setOptionsCount}
                        setOptions={setOptions}
                        refreshKey={refreshKey}
                        key={refreshKey}
                        onKeyDown={async (e) => {
                            if (e.key === 'Enter' &&
                                tagSearchValue?.trim().length &&
                                props.internalTags?.find((t) => t.name.toLowerCase() === tagSearchValue?.trim().toLowerCase()) == null &&
                                options?.find((t) => t.name.toLowerCase() === tagSearchValue?.trim().toLowerCase()) == null) {
                                    
                                const tag = {
                                    category: "chat_internal",
                                    name: tagSearchValue,
                                };
                                const res = await dataAPI(CREATE, tour_tag_resource, { data: tag });
                                if (res.statusCode === 200 || res.statusCode === 201) {
                                    let newArr = [...props.internalTags, res.data]
                                    internalTagsRef.current = newArr;
                                    props.setInternalTags(newArr);
                                    updateTags(newArr);

                                    setRefreshKey(refreshKey+1)
                                    enqueueSnackbar(`Tag "${tag.name}" created`, { variant: 'success' });
                                } else {
                                    enqueueSnackbar('Tag could not be created', { variant: 'error' });
                                }
                            }
                            else if (e.key == 'ArrowDown' || e.key == 'ArrowUp'){
                                e.stopPropagation()
                            }
                          }}
                        />
                </div>
            </StyledMenu>
        </span>
    );
}
